import { useEffect, useState } from "react";
import { useForm } from "@mantine/form";
import { Button, Group, TextInput, Select, NumberInput, ActionIcon, Title, Paper, Stack, Grid, Badge, Text, Checkbox } from "@mantine/core";
import { IconArrowLeft, IconDeviceFloppy, IconPlus, IconEdit } from "@tabler/icons-react";
import { ContractResponse, ContractCreateRequest, ContractUpdateRequest, ContractStatus, ContractListItem } from "../../../../types/UserRole";
import { createContract, updateContract, getContractStatuses, getTemplateContracts, getContractById, getVatOptions, getPaymentTermOptions, getFrameworkOptions } from "../../../../services/ContractService";
import { notifications } from "@mantine/notifications";
import styles from './ContractForm.module.css';
import { useTranslation } from "../../../../hooks/useTranslation";
import useTransientStore from "../../../../data/transcientStore";

interface AddContractFormProps {
    addressSystemId: number;
    onClose: () => void;
    onBack: () => void;
}

interface EditContractFormProps {
    contract: ContractResponse;
    addressSystemId: number;
    onClose: () => void;
    onBack: () => void;
}

export function AddContractForm({ addressSystemId, onClose, onBack }: AddContractFormProps) {
    const selectedAddressSystem = useTransientStore((state) => state.selectedAddressSystem);
    const addrIdFromStore = selectedAddressSystem?.id || 0;

    const { getTranslation } = useTranslation({ pageId: 315, addressSystemId: 0, autoLoad: true });
    const [loading, setLoading] = useState(false);
    const [contractStatuses, setContractStatuses] = useState<ContractStatus[]>([]);
    const [templateContracts, setTemplateContracts] = useState<ContractListItem[]>([]);
    const [vatOptions, setVatOptions] = useState<{ id: number; description: string; }[]>([]);
    const [paymentTermOptions, setPaymentTermOptions] = useState<{ id: number; description: string; }[]>([]);
    const [frameworkValues, setFrameworkValues] = useState<Record<number, string>>({});

    const form = useForm<{
        name: string;
        contractorName: string;
        addressSystemId: number;
        contractNumber: string;
        contractFrameworkNumber: string;
        contractStatusId: number | undefined;
        archiveEmail: string;
        totalPrice: number | undefined;
        totalArea: number | undefined;
        totalWeight: number | undefined;
        defaultInvoicingNumber: number | undefined;
        invoicingVatId: number | undefined;
        invoicingPaymentTermId: number | undefined;
        userTemplateContractId: number | undefined;
        templateContractId: number | undefined;
        // New fields
        siteCode: string;
        littera: string;
        accountCode: string;    
        archiveCode: string;
        contractorArchiveEmail: string;
        changesApproverEmail: string;
        contractCity: string;
        contractorStreetAddress: string;
        contractorCityAddress: string;
        contractorVatNumber: string;
        includeInReport: number;
        copyToRamapp: number;
    }>({
        mode: 'controlled',
        initialValues: {
            name: '',
            contractorName: '',
            addressSystemId: addressSystemId,
            contractNumber: '',
            contractFrameworkNumber: '',
            contractStatusId: undefined,
            archiveEmail: '',
            totalPrice: undefined,
            totalArea: undefined,
            totalWeight: undefined,
            defaultInvoicingNumber: undefined,
            invoicingVatId: undefined,
            invoicingPaymentTermId: undefined,
            userTemplateContractId: undefined,
            templateContractId: undefined,
            // New fields
            siteCode: '',
            littera: '',
            accountCode: '',
            archiveCode: '',
            contractorArchiveEmail: '',
            changesApproverEmail: '',
            contractCity: '',
            contractorStreetAddress: '',
            contractorCityAddress: '',
            contractorVatNumber: '',
            includeInReport: 0,
            copyToRamapp: 0,
        },

        validate: {
            name: (value: string) => {
                if (!value || value.trim() === '') return getTranslation("LABEL_CONTRACT_FORM_NAME_REQUIRED") || "Contract name is required";
                return null;
            },
            contractorName: (value: string) => {
                if (!value || value.trim() === '') return getTranslation("LABEL_CONTRACT_FORM_CONTRACTOR_REQUIRED") || "Contractor name is required";
                return null;
            },
            contractNumber: (value: string) => {
                if (!value || value.trim() === '') return getTranslation("LABEL_CONTRACT_FORM_NUMBER_REQUIRED") || "Contract number is required";
                return null;
            },
            contractCity: (value: string) => {
                if (!value || value.trim() === '') return getTranslation("LABEL_CONTRACT_FORM_CITY_REQUIRED") || "Contract city is required";
                return null;
            },
            archiveEmail: (value: string) => {
                if (value && value.trim() !== '') {
                    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
                    if (!emailRegex.test(value)) return getTranslation("LABEL_CONTRACT_FORM_EMAIL_INVALID") || "Please enter a valid email address";
                }
                return null;
            },
            contractorArchiveEmail: (value: string) => {
                if (value && value.trim() !== '') {
                    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
                    if (!emailRegex.test(value)) return getTranslation("LABEL_CONTRACT_FORM_EMAIL_INVALID") || "Please enter a valid email address";
                }
                return null;
            },
            changesApproverEmail: (value: string) => {
                if (value && value.trim() !== '') {
                    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
                    if (!emailRegex.test(value)) return getTranslation("LABEL_CONTRACT_FORM_EMAIL_INVALID") || "Please enter a valid email address";
                }
                return null;
            }
        }
    });

    useEffect(() => {
        fetchContractStatuses();
        fetchTemplateContracts();
        fetchVatOptions();
        fetchPaymentTermOptions();
    }, []);

    const fetchContractStatuses = async () => {
        try {
            const statuses = await getContractStatuses(addressSystemId);
            setContractStatuses(statuses);
        } catch (error) {
            notifications.show({
                title: getTranslation("LABEL_CONTRACT_FORM_WARNING_TITLE") || "Warning",
                message: getTranslation("LABEL_CONTRACT_FORM_STATUSES_LOAD_FAILED") || "Failed to load contract statuses",
                color: 'yellow',
                withBorder: true,
                withCloseButton: true,
                autoClose: 5000,
                className: 'notification-warning'
            });
        }
    };

    const fetchTemplateContracts = async () => {
        try {
            const templates = await getTemplateContracts(addressSystemId);
            setTemplateContracts(templates);
        } catch (error) {
            notifications.show({
                title: getTranslation("LABEL_CONTRACT_FORM_WARNING_TITLE") || "Warning",
                message: getTranslation("LABEL_CONTRACT_FORM_TEMPLATES_LOAD_FAILED") || "Failed to load contract templates",
                color: 'yellow',
                withBorder: true,
                withCloseButton: true,
                autoClose: 5000,
                className: 'notification-warning'
            });
        }
    };

    const handleTemplateSelection = async (templateId: string | null) => {
        if (!templateId) {
            form.setFieldValue('templateContractId', undefined);
            return;
        }

        try {
            const templateContract = await getContractById(Number(templateId), addressSystemId);

            // Fill form with template values, but keep the addressSystemId and templateContractId
            form.setValues({
                ...form.values,
                templateContractId: Number(templateId),
                contractorName: templateContract.contractorName || '',
                contractNumber: templateContract.contractNumber || '',
                contractFrameworkNumber: templateContract.contractFrameworkNumber || '',
                contractStatusId: templateContract.contractStatusId,
                archiveEmail: templateContract.archiveEmail || '',
                totalPrice: templateContract.totalPrice,
                totalArea: templateContract.totalArea,
                totalWeight: templateContract.totalWeight,
                defaultInvoicingNumber: templateContract.defaultInvoicingNumber,
                invoicingVatId: templateContract.invoicingVatId,
                invoicingPaymentTermId: templateContract.invoicingPaymentTermId,
                siteCode: templateContract.siteCode || '',
                littera: templateContract.littera || '',
                accountCode: templateContract.accountCode || '',
                archiveCode: templateContract.archiveCode || '',
                contractorArchiveEmail: templateContract.contractorArchiveEmail || '',
                changesApproverEmail: templateContract.changesApproverEmail || '',
                contractCity: templateContract.contractCity || '',
                contractorStreetAddress: templateContract.contractorStreetAddress || '',
                contractorCityAddress: templateContract.contractorCityAddress || '',
                contractorVatNumber: templateContract.contractorVatNumber || '',
                includeInReport: templateContract.includeInReport || 0,
                copyToRamapp: templateContract.copyToRamapp || 0,
                userTemplateContractId: Number(templateId)
            });

            notifications.show({
                title: getTranslation("LABEL_CONTRACT_FORM_SUCCESS_TITLE") || "Success",
                message: getTranslation("LABEL_CONTRACT_FORM_TEMPLATE_APPLIED") || `Template "${templateContract.name}" has been applied to the form`,
                color: 'green',
                withBorder: true,
                withCloseButton: true,
                autoClose: 5000,
                className: 'notification-success'
            });
        } catch (error) {
            notifications.show({
                title: getTranslation("LABEL_CONTRACT_FORM_ERROR_TITLE") || "Error",
                message: getTranslation("LABEL_CONTRACT_FORM_TEMPLATE_DETAILS_FAILED") || "Failed to load template contract details",
                color: 'red',
                withBorder: true,
                withCloseButton: true,
                autoClose: 5000,
                className: 'notification-error'
            });
        }
    };

    const fetchVatOptions = async () => {
        try {
            if (form.values.addressSystemId) {
                const response = await getVatOptions(form.values.addressSystemId);
                setVatOptions(response);
            }
        } catch (error) {
            console.error(getTranslation("LABEL_CONTRACT_FORM_VAT_OPTIONS_ERROR") || "Error fetching VAT options:", error);
        }
    };

    const fetchPaymentTermOptions = async () => {
        try {
            if (form.values.addressSystemId) {
                const response = await getPaymentTermOptions(form.values.addressSystemId);
                setPaymentTermOptions(response);
            }
        } catch (error) {
            console.error(getTranslation("LABEL_CONTRACT_FORM_PAYMENT_OPTIONS_ERROR") || "Error fetching payment term options:", error);
        }
    };

    const handleSubmit = async (values: typeof form.values) => {
        setLoading(true);
        try {
            const createRequest: ContractCreateRequest = {
                name: values.name.trim(),
                contractorName: values.contractorName?.trim() || undefined,
                addressSystemId: values.addressSystemId,
                contractNumber: values.contractNumber?.trim() || undefined,
                contractFrameworkNumber: values.contractFrameworkNumber?.trim() || undefined,
                contractStatusId: values.contractStatusId,
                archiveEmail: values.archiveEmail?.trim() || undefined,
                totalPrice: values.totalPrice,
                totalArea: values.totalArea,
                totalWeight: values.totalWeight,
                defaultInvoicingNumber: values.defaultInvoicingNumber,
                invoicingVatId: values.invoicingVatId,
                invoicingPaymentTermId: values.invoicingPaymentTermId,
                userTemplateContractId: values.userTemplateContractId !== undefined ? values.userTemplateContractId : 0,
                templateContractId: values.templateContractId !== undefined ? values.templateContractId : 0,
                siteCode: values.siteCode?.trim() || undefined,
                littera: values.littera?.trim() || undefined,
                accountCode: values.accountCode?.trim() || undefined,
                archiveCode: values.archiveCode?.trim() || undefined,
                contractorArchiveEmail: values.contractorArchiveEmail?.trim() || undefined,
                changesApproverEmail: values.changesApproverEmail?.trim() || undefined,
                contractCity: values.contractCity?.trim() || undefined,
                contractorStreetAddress: values.contractorStreetAddress?.trim() || undefined,
                contractorCityAddress: values.contractorCityAddress?.trim() || undefined,
                contractorVatNumber: values.contractorVatNumber?.trim() || undefined,
                includeInReport: values.includeInReport ? 1 : 0,
                copyToRamapp: values.copyToRamapp ? 1 : 0,
            };
            await createContract(createRequest);
            notifications.show({
                title: getTranslation("LABEL_CONTRACT_FORM_SUCCESS_TITLE") || "Success",
                message: getTranslation("LABEL_CONTRACT_FORM_CREATED") || "Contract created successfully",
                color: 'green',
                withBorder: true,
                withCloseButton: true,
                autoClose: 5000,
                className: 'notification-success'
            });
            onClose();
        } catch (error) {
            notifications.show({
                title: getTranslation("LABEL_CONTRACT_FORM_ERROR_TITLE") || "Error",
                message: getTranslation("LABEL_CONTRACT_FORM_CREATE_FAILED") || "Failed to create contract",
                color: 'red',
                withBorder: true,
                withCloseButton: true,
                autoClose: 5000,
                className: 'notification-error'
            });
        } finally {
            setLoading(false);
        }
    };

    return (
        <Paper p="lg" className={styles.formContainer}>
            <Group justify="space-between" mb="lg">
                <Group>
                    <ActionIcon
                        onClick={onBack}
                        variant="subtle"
                        size="lg"
                    >
                        <IconArrowLeft size={20} />
                    </ActionIcon>
                    <Title order={3} c="blue">
                        {getTranslation("LABEL_CONTRACT_FORM_ADD_NEW") || "Add New Contract"}
                    </Title>
                </Group>
                <Group>
                    <Button
                        variant="outline"
                        onClick={() => {
                            form.setValues({
                                name: '',
                                contractorName: '',
                                addressSystemId: addressSystemId,
                                contractNumber: '',
                                contractFrameworkNumber: '',
                                contractStatusId: undefined,
                                archiveEmail: '',
                                totalPrice: undefined,
                                totalArea: undefined,
                                totalWeight: undefined,
                                defaultInvoicingNumber: undefined,
                                invoicingVatId: undefined,
                                invoicingPaymentTermId: undefined,
                                userTemplateContractId: undefined,
                                templateContractId: undefined,
                                siteCode: '',
                                littera: '',
                                accountCode: '',
                                archiveCode: '',
                                contractorArchiveEmail: '',
                                changesApproverEmail: '',
                                contractCity: '',
                                contractorStreetAddress: '',
                                contractorCityAddress: '',
                                contractorVatNumber: '',
                                includeInReport: 0,
                                copyToRamapp: 0,
                            });
                            // Reset framework values as well
                            setFrameworkValues({});
                            // Show notification
                            notifications.show({
                                title: getTranslation("LABEL_CONTRACT_FORM_RESET_TITLE") || "Form reset",
                                message: getTranslation("LABEL_CONTRACT_FORM_RESET_MESSAGE") || "Form has been reset",
                                color: 'blue',
                                withBorder: true,
                                withCloseButton: true,
                                autoClose: 3000,
                                className: 'notification-info'
                            });
                        }}
                    >
                        {getTranslation("LABEL_CONTRACT_FORM_RESET") || "Reset Form"}
                    </Button>
                    <Button
                        type="submit"
                        loading={loading}
                        // leftSection={<IconDeviceFloppy size={16} />}
                        onClick={() => form.onSubmit(handleSubmit)()}
                    >
                        {getTranslation("LABEL_CONTRACT_FORM_CREATE") || "Create Contract"}
                    </Button>
                </Group>
            </Group>

            <form onSubmit={form.onSubmit(handleSubmit)}>
                <Grid>
                    {/* Left Column */}
                    <Grid.Col span={6}>
                        <Stack gap="xl">
                            <Stack gap={2}>
                                {/* Contract Details */}
                                <Title order={5}>{getTranslation("LABEL_CONTRACT_FORM_DETAILS") || "Contract Details"}</Title>
                                <Paper withBorder p="md" radius="md">
                                    <Stack gap="md">
                                        <Select
                                        // Template Contract
                                            label={getTranslation("LABEL_TEMPLATE_FOR_NEW_CONTRACT") || "Template for new contract"}
                                            placeholder={getTranslation("PLACEHOLDER_SELECT_TEMPLATE_CONTRACT") || "Select template contract"}
                                            data={templateContracts.map(tc => ({
                                                value: tc.id.toString(),
                                                label: tc.name
                                                // + (tc.contractStatusDefaultValue ? ' (Active)' : ' (Inactive)')
                                            }))}
                                            value={form.values.templateContractId !== undefined ? String(form.values.templateContractId) : null}
                                            onChange={handleTemplateSelection}
                                            clearable
                                            searchable
                                        />
                                        <TextInput
                                        // Contract Name
                                            label={getTranslation("LABEL_CONTRACT_NAME") || "Contract Name"}
                                            placeholder={getTranslation("PLACEHOLDER_ENTER_CONTRACT_NAME") || "Enter contract name"}
                                            required
                                            {...form.getInputProps('name')}
                                        />
                                        <TextInput
                                        // Contract Number
                                            label={getTranslation("LABEL_CONTRACT_NUMBER") || "Contract Number"}
                                            placeholder={getTranslation("PLACEHOLDER_ENTER_CONTRACT_NUMBER") || "Enter contract number"}
                                            required
                                            {...form.getInputProps('contractNumber')}
                                        />
                                        <TextInput
                                        // Contract Framework Number
                                            label={getTranslation("LABEL_CONTRACT_FRAMEWORK_NUMBER") || "Contract Framework Number"}
                                            placeholder={getTranslation("PLACEHOLDER_ENTER_FRAMEWORK_NUMBER") || "Enter framework number"}
                                            {...form.getInputProps('contractFrameworkNumber')}
                                        />
                                        <Select
                                        //Contract Status
                                            label={getTranslation("LABEL_STATUS") || "Status"}
                                            placeholder={getTranslation("PLACEHOLDER_SELECT_CONTRACT_STATUS") || "Select contract status"}
                                            data={contractStatuses.map(status => ({
                                                value: status.id.toString(),
                                                label: status.name
                                            }))}
                                            value={form.values.contractStatusId?.toString() || null}
                                            onChange={(value) => form.setFieldValue('contractStatusId', value ? parseInt(value) : undefined)}
                                            clearable
                                        />
                                    </Stack>
                                </Paper>
                            </Stack>

                            <Stack gap={2}>
                                {/* System & Internal References */}
                                <Title order={5}>{getTranslation("LABEL_CONTRACT_FORM_SYSTEM_INTERNAL") || "System & Internal References"}</Title>
                                <Paper withBorder p="md" radius="md">
                                    <Stack gap="md">
                                        <TextInput
                                        // Site Code
                                            label={getTranslation("LABEL_SITE_CODE_SITE_KEY") || "Site Code/ Site Key"}
                                            placeholder={getTranslation("PLACEHOLDER_ENTER_SITE_CODE_OR_KEY") || "Enter site code or key"}
                                            {...form.getInputProps('siteCode')}
                                        />
                                        <TextInput
                                        // Littera
                                            label={getTranslation("LABEL_LETTER") || "Letter"}
                                            placeholder={getTranslation("PLACEHOLDER_ENTER_LETTER") || "Enter letter"}
                                            {...form.getInputProps('littera')}
                                        />
                                        <TextInput
                                        // Account Code
                                            label={getTranslation("LABEL_ACCOUNT") || "Account"}
                                            placeholder={getTranslation("PLACEHOLDER_ENTER_ACCOUNT_CODE") || "Enter account code"}
                                            {...form.getInputProps('accountCode')}
                                        />
                                    </Stack>
                                </Paper>
                            </Stack>

                            <Stack gap={2}>
                                {/* Logistics & Archiving */}
                                <Title order={5}>{getTranslation("LABEL_CONTRACT_FORM_LOGISTICS_ARCHIVING") || "Logistics & Archiving"}</Title>
                                <Paper withBorder p="md" radius="md">
                                    <Stack gap="md">
                                        <NumberInput
                                        // Total Area
                                            label={getTranslation("LABEL_TOTAL_AREA_M") || "Total Area (m²)"}
                                            placeholder={getTranslation("PLACEHOLDER_ENTER_TOTAL_AREA") || "Enter total area"}
                                            decimalScale={2}
                                            min={0}
                                            hideControls
                                            {...form.getInputProps('totalArea')}
                                        />
                                        <NumberInput
                                        // Total Weight
                                            label={getTranslation("LABEL_TOTAL_WEIGHT_TN") || "Total Weight (tn)"}
                                            placeholder={getTranslation("PLACEHOLDER_ENTER_TOTAL_WEIGHT") || "Enter total weight"}
                                            decimalScale={2}
                                            min={0}
                                            hideControls
                                            {...form.getInputProps('totalWeight')}
                                        />
                                        <TextInput
                                        // Archive Code
                                            label={getTranslation("LABEL_ARCHIVING_NUMBER_ARCHIVING_ID") || "Archiving Number/ Archiving ID"}
                                            placeholder={getTranslation("PLACEHOLDER_ENTER_ARCHIVING_NUMBER_OR_ID") || "Enter archiving number or id"}
                                            {...form.getInputProps('archiveCode')}
                                        />
                                        <TextInput
                                        // Archive Email
                                            label={getTranslation("LABEL_ARCHIVE_EMAIL") || "Archive Email"}
                                            placeholder={getTranslation("PLACEHOLDER_ENTER_ARCHIVE_EMAIL") || "Enter archive email"}
                                            type="email"
                                            {...form.getInputProps('archiveEmail')}
                                        />
                                        <TextInput
                                        // Contractor Archive Email
                                            label={getTranslation("LABEL_CONTRACTOR_ARCHIVE_EMAIL") || "Contractor Archive Email"}
                                            placeholder={getTranslation("PLACEHOLDER_ENTER_CONTRACTOR_ARCHIVE_EMAIL") || "Enter contractor archive email"}
                                            type="email"
                                            {...form.getInputProps('contractorArchiveEmail')}
                                        />
                                        <TextInput
                                        // Changes Approver Email
                                            label={getTranslation("LABEL_CHANGES_APPROVER_EMAIL") || "Changes Approver Email"}
                                            placeholder={getTranslation("PLACEHOLDER_ENTER_CHANGES_APPROVER_EMAIL") || "Enter changes approver email"}
                                            type="email"
                                            {...form.getInputProps('changesApproverEmail')}
                                        />
                                    </Stack>
                                </Paper>
                            </Stack>
                        </Stack>
                    </Grid.Col>

                    {/* Right Column */}
                    <Grid.Col span={6}>
                        <Stack gap="xl">
                            <Stack gap={2}>
                                {/* Contractor Info */}
                                <Title order={5}>{getTranslation("LABEL_CONTRACT_FORM_CONTRACTOR_INFO") || "Contractor Info"}</Title>
                                <Paper withBorder p="md" radius="md">
                                    <Stack gap="md">
                                        <TextInput
                                        // Contractor Name
                                            label={getTranslation("LABEL_CONTRACTOR") || "Contractor"}
                                            placeholder={getTranslation("PLACEHOLDER_ENTER_CONTRACTOR_NAME") || "Enter contractor name"}
                                            required
                                            {...form.getInputProps('contractorName')}
                                        />
                                        <TextInput
                                        // Contract City
                                            label={getTranslation("LABEL_CITY_NAME") || "City Name"}
                                            placeholder={getTranslation("PLACEHOLDER_ENTER_CITY_NAME") || "Enter city name"}
                                            required
                                            {...form.getInputProps('contractCity')}
                                        />
                                        <TextInput
                                        // Contractor Street Address
                                            label={getTranslation("LABEL_STREET_ADDRESS") || "Street Address"}
                                            placeholder={getTranslation("PLACEHOLDER_ENTER_STREET_ADDRESS") || "Enter street address"}
                                            {...form.getInputProps('contractorStreetAddress')}
                                        />
                                        <TextInput
                                        // Contractor City Address
                                            label={getTranslation("LABEL_CITY_ADDRESS") || "City Address"}
                                            placeholder={getTranslation("PLACEHOLDER_ENTER_CITY_ADDRESS") || "Enter city address"}
                                            {...form.getInputProps('contractorCityAddress')}
                                        />
                                        <TextInput
                                        // Contractor VAT Number
                                            label={getTranslation("LABEL_VAT_NUMBER") || "VAT Number"}
                                            placeholder={getTranslation("PLACEHOLDER_ENTER_VAT_NUMBER") || "Enter VAT number"}
                                            {...form.getInputProps('contractorVatNumber')}
                                        />
                                    </Stack>
                                </Paper>
                            </Stack>

                            <Stack gap={2}>
                                {/* Financial Info */}
                                <Title order={5}>{getTranslation("LABEL_CONTRACT_FORM_FINANCIAL_INFO") || "Financial Info"}</Title>
                                <Paper withBorder p="md" radius="md">
                                    <Stack gap="md">
                                        <NumberInput
                                        // Default Invoicing Number
                                            label={getTranslation("LABEL_INVOICING_NUMBER") || "Invoicing Number"}
                                            placeholder={getTranslation("PLACEHOLDER_ENTER_INVOICING_NUMBER") || "Enter invoicing number"}
                                            min={0}
                                            hideControls
                                            {...form.getInputProps('defaultInvoicingNumber')}
                                        />
                                        <NumberInput
                                        // Total Price
                                            label={getTranslation("LABEL_TOTAL_VALUE_") || "Total Value (€)"}
                                            placeholder={getTranslation("PLACEHOLDER_ENTER_TOTAL_VALUE") || "Enter total value"}
                                            decimalScale={2}
                                            min={0}
                                            hideControls
                                            {...form.getInputProps('totalPrice')}
                                        />
                                        <Select
                                        // Invoicing VAT ID
                                            label={getTranslation("LABEL_VAT") || "VAT"}
                                            placeholder={getTranslation("PLACEHOLDER_SELECT_VAT") || "Select VAT"}
                                            data={vatOptions.map(option => ({
                                                value: option.id.toString(),
                                                label: option.description
                                            }))}
                                            value={form.values.invoicingVatId?.toString() || null}
                                            onChange={(value) => form.setFieldValue('invoicingVatId', value ? parseInt(value) : undefined)}
                                            searchable
                                            clearable
                                        />
                                        <Select
                                        // Invoicing Payment Term ID
                                            label={getTranslation("LABEL_RECEIPT") || "Receipt"}
                                            placeholder={getTranslation("PLACEHOLDER_SELECT_RECEIPT") || "Select receipt"}
                                            data={paymentTermOptions.map(option => ({
                                                value: option.id.toString(),
                                                label: option.description
                                            }))}
                                            value={form.values.invoicingPaymentTermId?.toString() || null}
                                            onChange={(value) => form.setFieldValue('invoicingPaymentTermId', value ? parseInt(value) : undefined)}
                                            searchable
                                            clearable
                                        />
                                    </Stack>
                                </Paper>
                            </Stack>

                            <Stack gap={2}>
                                {/* Automation & Workflows */}
                                <Title order={5}>{getTranslation("LABEL_CONTRACT_FORM_AUTOMATION_WORKFLOWS") || "Automation & Workflows"}</Title>
                                <Paper withBorder p="md" radius="md">
                                    <Stack gap="md">
                                        <Checkbox
                                        // Include In Report
                                            label={getTranslation("LABEL_INCLUDE_IN_SUMMARIES") || "Include In Summaries"}
                                            {...form.getInputProps('includeInReport', { type: 'checkbox' })}
                                        />
                                        <Checkbox
                                        // Copy To Ramapp
                                            label={getTranslation("LABEL_COPY_TO_RAMAPP") || "Copy To Ramapp"}
                                            {...form.getInputProps('copyToRamapp', { type: 'checkbox' })}
                                        />
                                        <Select
                                        // User Template Contract
                                            label={getTranslation("LABEL_COPY_USERS_FROM_TEMPLATE_CONTRACT") || "Copy users from Template Contract"}
                                            placeholder={getTranslation("PLACEHOLDER_SELECT_TEMPLATE_CONTRACT") || "Select template contract"}
                                            data={templateContracts.map(tc => ({
                                                value: tc.id.toString(),
                                                label: tc.name
                                                // + (tc.contractStatusDefaultValue ? ' (Active)' : ' (Inactive)')
                                            }))}
                                            value={form.values.userTemplateContractId !== undefined ? String(form.values.userTemplateContractId) : null}
                                            onChange={value => form.setFieldValue('userTemplateContractId', value ? Number(value) : undefined)}
                                            clearable
                                        />
                                    </Stack>
                                </Paper>
                            </Stack>
                        </Stack>
                    </Grid.Col>
                </Grid>
            </form>
        </Paper>
    );
}

export function EditContractForm({ contract, addressSystemId, onClose, onBack }: EditContractFormProps) {
    const { getTranslation } = useTranslation({ pageId: 315, addressSystemId: 0, autoLoad: true });
    const [loading, setLoading] = useState(false);
    const [contractStatuses, setContractStatuses] = useState<ContractStatus[]>([]);
    const [templateContracts, setTemplateContracts] = useState<ContractListItem[]>([]);
    const [vatOptions, setVatOptions] = useState<{ id: number; description: string; }[]>([]);
    const [paymentTermOptions, setPaymentTermOptions] = useState<{ id: number; description: string; }[]>([]);
    const [frameworkOptions, setFrameworkOptions] = useState<{ id: number; description: string; valueStr?: string; }[]>([]);
    const [frameworkValues, setFrameworkValues] = useState<Record<number, string>>({});

    const form = useForm<{
        name: string;
        contractorName: string;
        addressSystemId: number;
        contractNumber: string;
        contractFrameworkNumber: string;
        contractStatusId: number | undefined;
        contratStatusName?: string;
        archiveEmail: string;
        totalPrice: number | undefined;
        totalArea: number | undefined;
        totalWeight: number | undefined;
        defaultInvoicingNumber: number | undefined;
        invoicingVatId: number | undefined;
        invoicingPaymentTermId: number | undefined;
        userTemplateContractId: number | undefined;
        templateContractId: number | undefined;
        siteCode: string;
        littera: string;
        accountCode: string;
        archiveCode: string;
        contractorArchiveEmail: string;
        changesApproverEmail: string;
        contractCity: string;
        contractorStreetAddress: string;
        contractorCityAddress: string;
        contractorVatNumber: string;
        includeInReport: number;
        copyToRamapp: number;
    }>({
        mode: 'controlled',
        initialValues: {
            name: contract.name || '',
            contractorName: contract.contractorName || '',
            addressSystemId: addressSystemId,
            contractNumber: contract.contractNumber || '',
            contractFrameworkNumber: contract.contractFrameworkNumber || '',
            contractStatusId: contract.contractStatusId || undefined,
            contratStatusName: contract.contractStatusName || undefined,
            archiveEmail: contract.archiveEmail || '',
            totalPrice: contract.totalPrice || undefined,
            totalArea: contract.totalArea || undefined,
            totalWeight: contract.totalWeight || undefined,
            defaultInvoicingNumber: contract.defaultInvoicingNumber || undefined,
            invoicingVatId: contract.invoicingVatId || undefined,
            invoicingPaymentTermId: contract.invoicingPaymentTermId || undefined,
            userTemplateContractId: undefined,
            templateContractId: undefined,
            siteCode: contract.siteCode || '',
            littera: contract.littera || '',
            accountCode: contract.accountCode || '',
            archiveCode: contract.archiveCode || '',
            contractorArchiveEmail: contract.contractorArchiveEmail || '',
            changesApproverEmail: contract.changesApproverEmail || '',
            contractCity: contract.contractCity || '',
            contractorStreetAddress: contract.contractorStreetAddress || '',
            contractorCityAddress: contract.contractorCityAddress || '',
            contractorVatNumber: contract.contractorVatNumber || '',
            includeInReport: typeof contract.includeInReport !== 'undefined' ? contract.includeInReport : 0,
            copyToRamapp: typeof contract.copyToRamapp !== 'undefined' ? contract.copyToRamapp : 0,
        },

        validate: {
            name: (value: string) => {
                if (!value || value.trim() === '') return getTranslation("LABEL_CONTRACT_FORM_NAME_REQUIRED") || "Contract name is required";
                return null;
            },
            contractorName: (value: string) => {
                if (!value || value.trim() === '') return getTranslation("LABEL_CONTRACT_FORM_CONTRACTOR_REQUIRED") || "Contractor name is required";
                return null;
            },
            contractNumber: (value: string) => {
                if (!value || value.trim() === '') return getTranslation("LABEL_CONTRACT_FORM_NUMBER_REQUIRED") || "Contract number is required";
                return null;
            },
            contractCity: (value: string) => {
                if (!value || value.trim() === '') return getTranslation("LABEL_CONTRACT_FORM_CITY_REQUIRED") || "Contract city is required";
                return null;
            },
            archiveEmail: (value: string) => {
                if (value && value.trim() !== '') {
                    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
                    if (!emailRegex.test(value)) return getTranslation("LABEL_CONTRACT_FORM_EMAIL_INVALID") || "Please enter a valid email address";
                }
                return null;
            },
            contractorArchiveEmail: (value: string) => {
                if (value && value.trim() !== '') {
                    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
                    if (!emailRegex.test(value)) return getTranslation("LABEL_CONTRACT_FORM_EMAIL_INVALID") || "Please enter a valid email address";
                }
                return null;
            },
            changesApproverEmail: (value: string) => {
                if (value && value.trim() !== '') {
                    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
                    if (!emailRegex.test(value)) return getTranslation("LABEL_CONTRACT_FORM_EMAIL_INVALID") || "Please enter a valid email address";
                }
                return null;
            }
        }
    });

    useEffect(() => {
        fetchContractStatuses();
        fetchTemplateContracts();
        fetchVatOptions();
        fetchPaymentTermOptions();
        // Fetch framework options for the existing contract
        if (contract.id) {
            fetchFrameworkOptions(contract.id);
        }
    }, []);

    const fetchContractStatuses = async () => {
        try {
            const statuses = await getContractStatuses(addressSystemId);
            setContractStatuses(statuses);
        } catch (error) {
            notifications.show({
                title: getTranslation("LABEL_CONTRACT_FORM_WARNING_TITLE") || "Warning",
                message: getTranslation("LABEL_CONTRACT_FORM_STATUSES_LOAD_FAILED") || "Failed to load contract statuses",
                color: 'yellow',
                withBorder: true,
                withCloseButton: true,
                autoClose: 5000,
                className: 'notification-warning'
            });
        }
    };

    const fetchTemplateContracts = async () => {
        try {
            const templates = await getTemplateContracts(addressSystemId);
            setTemplateContracts(templates);
        } catch (error) {
            notifications.show({
                title: getTranslation("LABEL_CONTRACT_FORM_WARNING_TITLE") || "Warning",
                message: getTranslation("LABEL_CONTRACT_FORM_TEMPLATES_LOAD_FAILED") || "Failed to load contract templates",
                color: 'yellow',
                withBorder: true,
                withCloseButton: true,
                autoClose: 5000,
                className: 'notification-warning'
            });
        }
    };

    const fetchVatOptions = async () => {
        try {
            const options = await getVatOptions(addressSystemId);
            setVatOptions(options);
        } catch (error) {
            notifications.show({
                title: getTranslation("LABEL_CONTRACT_FORM_WARNING_TITLE") || "Warning",
                message: getTranslation("LABEL_CONTRACT_FORM_VAT_LOAD_FAILED") || "Failed to load VAT options",
                color: 'yellow',
                withBorder: true,
                withCloseButton: true,
                autoClose: 5000,
                className: 'notification-warning'
            });
        }
    };

    const fetchPaymentTermOptions = async () => {
        try {
            const options = await getPaymentTermOptions(addressSystemId);
            setPaymentTermOptions(options);
        } catch (error) {
            notifications.show({
                title: getTranslation("LABEL_CONTRACT_FORM_WARNING_TITLE") || "Warning",
                message: getTranslation("LABEL_CONTRACT_FORM_PAYMENT_LOAD_FAILED") || "Failed to load payment term options",
                color: 'yellow',
                withBorder: true,
                withCloseButton: true,
                autoClose: 5000,
                className: 'notification-warning'
            });
        }
    };

    const fetchFrameworkOptions = async (contractId: number) => {
        try {
            const response = await getFrameworkOptions(contractId);
            setFrameworkOptions(response);
            
            // Initialize framework values from response
            const initialValues: Record<number, string> = {};
            response.forEach((option: { id: number; valueStr?: string }) => {
                if (option.valueStr) {
                    initialValues[option.id] = option.valueStr;
                }
            });
            setFrameworkValues(initialValues);
        } catch (error) {
            notifications.show({
                title: getTranslation("LABEL_CONTRACT_FORM_WARNING_TITLE") || "Warning",
                message: getTranslation("LABEL_CONTRACT_FORM_FRAMEWORK_LOAD_FAILED") || "Failed to load framework options",
                color: 'yellow',
                withBorder: true,
                withCloseButton: true,
                autoClose: 5000,
                className: 'notification-warning'
            });
        }
    };

    const handleSubmit = async (values: typeof form.values) => {
        setLoading(true);
        try {
            const updateRequest: ContractUpdateRequest = {
                id: contract.id,
                name: values.name.trim(),
                contractorName: values.contractorName?.trim() || undefined,
                contractCity: values.contractCity?.trim() || undefined,
                addressSystemId: values.addressSystemId,
                contractNumber: values.contractNumber?.trim() || undefined,
                contractFrameworkNumber: values.contractFrameworkNumber?.trim() || undefined,
                contractStatusId: values.contractStatusId,
                archiveEmail: values.archiveEmail?.trim() || undefined,
                totalPrice: values.totalPrice,
                totalArea: values.totalArea,
                totalWeight: values.totalWeight,
                defaultInvoicingNumber: values.defaultInvoicingNumber,
                invoicingVatId: values.invoicingVatId,
                invoicingPaymentTermId: values.invoicingPaymentTermId,
                userTemplateContractId: values.userTemplateContractId !== undefined ? values.userTemplateContractId : 0,
                includeInReport: values.includeInReport ? 1 : 0,
                copyToRamapp: values.copyToRamapp ? 1 : 0,
                contractorStreetAddress: values.contractorStreetAddress?.trim() || undefined,
                contractorCityAddress: values.contractorCityAddress?.trim() || undefined,
                contractorVatNumber: values.contractorVatNumber?.trim() || undefined,
                // templateContractId: values.templateContractId,
                contractorArchiveEmail: values.contractorArchiveEmail?.trim() || undefined,
                changesApproverEmail: values.changesApproverEmail?.trim() || undefined,
                archiveCode: values.archiveCode?.trim() || undefined,
                accountCode: values.accountCode?.trim() || undefined,
                siteCode: values.siteCode?.trim() || undefined,
                littera: values.littera?.trim() || undefined,
                frameworkNumbers: frameworkValues,
            };
            await updateContract(updateRequest);
            notifications.show({
                title: getTranslation("LABEL_CONTRACT_FORM_SUCCESS_TITLE") || "Success",
                message: getTranslation("LABEL_CONTRACT_FORM_UPDATED") || "Contract updated successfully",
                color: 'green',
                withBorder: true,
                withCloseButton: true,
                autoClose: 5000,
                className: 'notification-success'
            });
            onClose();
        } catch (error) {
            notifications.show({
                title: getTranslation("LABEL_CONTRACT_FORM_ERROR_TITLE") || "Error",
                message: getTranslation("LABEL_CONTRACT_FORM_UPDATE_FAILED") || "Failed to update contract",
                color: 'red',
                withBorder: true,
                withCloseButton: true,
                autoClose: 5000,
                className: 'notification-error'
            });
        } finally {
            setLoading(false);
        }
    };

    return (
        <Paper p="lg" className={styles.formContainer}>
            <Group justify="space-between" mb="lg">
                <Group>
                    <ActionIcon
                        onClick={onBack}
                        variant="subtle"
                        size="lg"
                    >
                        <IconArrowLeft size={20} />
                    </ActionIcon>
                    <Title order={3} c="blue">
                        {/* <IconEdit size={24} className={styles.editIcon} /> */}
                        {getTranslation("LABEL_CONTRACT_FORM_EDIT") || "Edit Contract"}
                    </Title>
                    {/* <Badge variant="light" color="blue">ID: {contract.id}</Badge> */}
                </Group>
                <Group>
                    <Button
                        variant="outline"
                        onClick={() => {
                            form.setValues({
                                name: contract.name || '',
                                contractorName: contract.contractorName || '',
                                addressSystemId: addressSystemId,
                                contractNumber: contract.contractNumber || '',
                                contractFrameworkNumber: contract.contractFrameworkNumber || '',
                                contractStatusId: contract.contractStatusId || undefined,
                                archiveEmail: contract.archiveEmail || '',
                                totalPrice: contract.totalPrice || undefined,
                                totalArea: contract.totalArea || undefined,
                                totalWeight: contract.totalWeight || undefined,
                                defaultInvoicingNumber: contract.defaultInvoicingNumber || undefined,
                                invoicingVatId: contract.invoicingVatId || undefined,
                                invoicingPaymentTermId: contract.invoicingPaymentTermId || undefined,
                                userTemplateContractId: undefined, //check
                                templateContractId: undefined,
                                siteCode: contract.siteCode || '',
                                littera: contract.littera || '',
                                accountCode: contract.accountCode || '',
                                archiveCode: contract.archiveCode || '',
                                contractorArchiveEmail: contract.contractorArchiveEmail || '',
                                changesApproverEmail: contract.changesApproverEmail || '',
                                contractCity: contract.contractCity || '',
                                contractorStreetAddress: contract.contractorStreetAddress || '',
                                contractorCityAddress: contract.contractorCityAddress || '',
                                contractorVatNumber: contract.contractorVatNumber || '',
                                includeInReport: typeof contract.includeInReport !== 'undefined' ? contract.includeInReport : 0,
                                copyToRamapp: typeof contract.copyToRamapp !== 'undefined' ? contract.copyToRamapp : 0,
                            });
                            // Reset framework values back to original values from contract
                            if (contract.id) {
                                fetchFrameworkOptions(contract.id);
                            }
                            // Show notification
                            notifications.show({
                                title: getTranslation("LABEL_CONTRACT_FORM_ALL_CHANGES_RESET_TITLE") || "All changes reset",
                                message: getTranslation("LABEL_CONTRACT_FORM_ALL_CHANGES_RESET_MESSAGE") || "Form has been reset to original values",
                                color: 'blue',
                                withBorder: true,
                                withCloseButton: true,
                                autoClose: 3000,
                                className: 'notification-info'
                            });
                        }}
                    >
                        {getTranslation("LABEL_CONTRACT_FORM_RESET_CHANGES") || "Reset Changes"}
                    </Button>
                    <Button
                        type="submit"
                        loading={loading}
                        // leftSection={<IconDeviceFloppy size={16} />}
                        onClick={() => form.onSubmit(handleSubmit)()}
                    >
                        {getTranslation("LABEL_CONTRACT_FORM_UPDATE") || "Update Contract"}
                    </Button>
                </Group>
            </Group>

            <Paper withBorder p="sm" mb="lg" bg="gray.0">
                <Group justify="space-between">
                    <Group>
                        <Text size="sm" fw={500}>
                            {getTranslation("LABEL_CONTRACT_FORM_ADDRESS_SYSTEM") || "Address System:"}
                        </Text>
                        {/* <Text size="sm">{contract.addressSystemName}</Text> */}
                        <Badge variant="light" color="blue">{contract.addressSystemName}</Badge>
                    </Group>
                    <Group>
                        <Text size="sm" fw={500}>
                            {getTranslation("LABEL_CONTRACT_FORM_CURRENT_STATUS") || "Current Status:"}
                        </Text>
                        <Badge variant="light">
                            {contract.contractStatusName || (getTranslation("LABEL_CONTRACT_FORM_NO_STATUS") || "No Status")}
                        </Badge>
                        {/* <Text size="sm">{contract.contractStatusName}</Text> */}
                    </Group>
                </Group>
            </Paper>

            <form onSubmit={form.onSubmit(handleSubmit)}>
                <Grid>
                    {/* Left Column */}
                    <Grid.Col span={6}>
                        <Stack gap="xl">
                            <Stack gap={2}>
                                {/* Contract Details */}
                                <Title order={5}>{getTranslation("LABEL_CONTRACT_FORM_DETAILS") || "Contract Details"}</Title>
                                <Paper withBorder p="md" radius="md">
                                    <Stack gap="md">
                                        <TextInput
                                        // Contract Name
                                            label={getTranslation("LABEL_CONTRACT_NAME") || "Contract Name"}
                                            placeholder={getTranslation("PLACEHOLDER_ENTER_CONTRACT_NAME") || "Enter contract name"}
                                            required
                                            {...form.getInputProps('name')}
                                        />
                                        <TextInput
                                        // Contract Number
                                            label={getTranslation("LABEL_CONTRACT_NUMBER") || "Contract Number"}
                                            placeholder={getTranslation("PLACEHOLDER_ENTER_CONTRACT_NUMBER") || "Enter contract number"}
                                            required
                                            {...form.getInputProps('contractNumber')}
                                        />
                                        <TextInput
                                        // Contract Framework Number
                                            label={getTranslation("LABEL_CONTRACT_FRAMEWORK_NUMBER") || "Contract Framework Number"}
                                            placeholder={getTranslation("PLACEHOLDER_ENTER_FRAMEWORK_NUMBER") || "Enter framework number"}
                                            {...form.getInputProps('contractFrameworkNumber')}
                                        />
                                        <Select
                                        //Contract Status
                                            label={getTranslation("LABEL_STATUS") || "Status"}
                                            placeholder={getTranslation("PLACEHOLDER_SELECT_CONTRACT_STATUS") || "Select contract status"}
                                            data={contractStatuses.map(status => ({
                                                value: status.id.toString(),
                                                label: status.name
                                            }))}
                                            value={form.values.contractStatusId?.toString() || null}
                                            onChange={(value) => form.setFieldValue('contractStatusId', value ? parseInt(value) : undefined)}
                                            clearable
                                        />
                                    </Stack>
                                </Paper>
                            </Stack>

                            {/* Contract Framework Numbers Section */}
                            {frameworkOptions.length > 0 && (
                                <Stack gap={2}>
                                    <Title order={5}>{getTranslation("LABEL_CONTRACT_FORM_FRAMEWORK_NUMBERS") || "Contract Framework Numbers"}</Title>
                                    <Paper withBorder p="md" radius="md">
                                        <Stack gap="md">
                                            {frameworkOptions.map((option) => (
                                                <TextInput
                                                    key={option.id}
                                                    label={option.description}
                                                    placeholder={getTranslation("PLACEHOLDER_CONTRACT_FRAMEWORK_VALUE") || `Enter value for ${option.description}`}
                                                    value={frameworkValues[option.id] || ''}
                                                    onChange={(event) => 
                                                        setFrameworkValues(prev => ({
                                                            ...prev,
                                                            [option.id]: event.currentTarget.value
                                                        }))
                                                    }
                                                />
                                            ))}
                                        </Stack>
                                    </Paper>
                                </Stack>
                            )}

                            <Stack gap={2}>
                                {/* System & Internal References */}
                                <Title order={5}>{getTranslation("LABEL_CONTRACT_FORM_SYSTEM_INTERNAL") || "System & Internal References"}</Title>
                                <Paper withBorder p="md" radius="md">
                                    <Stack gap="md">
                                        <TextInput
                                        // Site Code
                                            label={getTranslation("LABEL_SITE_CODE_SITE_KEY") || "Site Code/ Site Key"}
                                            placeholder={getTranslation("PLACEHOLDER_ENTER_SITE_CODE_OR_KEY") || "Enter site code or key"}
                                            {...form.getInputProps('siteCode')}
                                        />
                                        <TextInput
                                        // Littera
                                            label={getTranslation("LABEL_LETTER") || "Letter"}
                                            placeholder={getTranslation("PLACEHOLDER_ENTER_LETTER") || "Enter letter"}
                                            {...form.getInputProps('littera')}
                                        />
                                        <TextInput
                                        // Account Code
                                            label={getTranslation("LABEL_ACCOUNT") || "Account"}
                                            placeholder={getTranslation("PLACEHOLDER_ENTER_ACCOUNT_CODE") || "Enter account code"}
                                            {...form.getInputProps('accountCode')}
                                        />
                                    </Stack>
                                </Paper>
                            </Stack>

                            <Stack gap={2}>
                                {/* Logistics & Archiving */}
                                <Title order={5}>{getTranslation("LABEL_CONTRACT_FORM_LOGISTICS_ARCHIVING") || "Logistics & Archiving"}</Title>
                                <Paper withBorder p="md" radius="md">
                                    <Stack gap="md">
                                        <NumberInput
                                        // Total Area
                                            label={getTranslation("LABEL_TOTAL_AREA_M") || "Total Area (m²)"}
                                            placeholder={getTranslation("PLACEHOLDER_ENTER_TOTAL_AREA") || "Enter total area"}
                                            decimalScale={2}
                                            min={0}
                                            hideControls
                                            {...form.getInputProps('totalArea')}
                                        />
                                        <NumberInput
                                        // Total Weight
                                            label={getTranslation("LABEL_TOTAL_WEIGHT_TN") || "Total Weight (tn)"}
                                            placeholder={getTranslation("PLACEHOLDER_ENTER_TOTAL_WEIGHT") || "Enter total weight"}
                                            decimalScale={2}
                                            min={0}
                                            hideControls
                                            {...form.getInputProps('totalWeight')}
                                        />
                                        <TextInput
                                        // Archive Code
                                            label={getTranslation("LABEL_ARCHIVING_NUMBER_ARCHIVING_ID") || "Archiving Number/ Archiving ID"}
                                            placeholder={getTranslation("PLACEHOLDER_ENTER_ARCHIVING_NUMBER_OR_ID") || "Enter archiving number or id"}
                                            {...form.getInputProps('archiveCode')}
                                        />
                                        <TextInput
                                        // Archive Email
                                            label={getTranslation("LABEL_ARCHIVE_EMAIL") || "Archive Email"}
                                            placeholder={getTranslation("PLACEHOLDER_ENTER_ARCHIVE_EMAIL") || "Enter archive email"}
                                            type="email"
                                            {...form.getInputProps('archiveEmail')}
                                        />
                                        <TextInput
                                        // Contractor Archive Email
                                            label={getTranslation("LABEL_CONTRACTOR_ARCHIVE_EMAIL") || "Contractor Archive Email"}
                                            placeholder={getTranslation("PLACEHOLDER_ENTER_CONTRACTOR_ARCHIVE_EMAIL") || "Enter contractor archive email"}
                                            type="email"
                                            {...form.getInputProps('contractorArchiveEmail')}
                                        />
                                        <TextInput
                                        // Changes Approver Email
                                            label={getTranslation("LABEL_CHANGES_APPROVER_EMAIL") || "Changes Approver Email"}
                                            placeholder={getTranslation("PLACEHOLDER_ENTER_CHANGES_APPROVER_EMAIL") || "Enter changes approver email"}
                                            type="email"
                                            {...form.getInputProps('changesApproverEmail')}
                                        />
                                    </Stack>
                                </Paper>
                            </Stack>
                        </Stack>
                    </Grid.Col>

                    {/* Right Column */}
                    <Grid.Col span={6}>
                        <Stack gap="xl">
                            {/* Contractor Info */}
                            <Stack gap={2}>
                                <Title order={5}>{getTranslation("LABEL_CONTRACT_FORM_CONTRACTOR_INFO") || "Contractor Info"}</Title>
                                <Paper withBorder p="md" radius="md">
                                    <Stack gap="md">
                                        <TextInput
                                        // Contractor Name
                                            label={getTranslation("LABEL_NAME") || "Name"}
                                            placeholder={getTranslation("PLACEHOLDER_ENTER_CONTRACTOR_NAME") || "Enter contractor name"}
                                            required
                                            {...form.getInputProps('contractorName')}
                                        />
                                        <TextInput
                                        // Contract City
                                            label={getTranslation("LABEL_CITY_NAME") || "City Name"}
                                            placeholder={getTranslation("PLACEHOLDER_ENTER_CITY_NAME") || "Enter city name"}
                                            required
                                            {...form.getInputProps('contractCity')}
                                        />
                                        <TextInput
                                        // Contractor Street Address
                                            label={getTranslation("LABEL_STREET_ADDRESS") || "Street Address"}
                                            placeholder={getTranslation("PLACEHOLDER_ENTER_STREET_ADDRESS") || "Enter street address"}
                                            {...form.getInputProps('contractorStreetAddress')}
                                        />
                                        <TextInput
                                        // Contractor City Address
                                            label={getTranslation("LABEL_CITY_ADDRESS") || "City Address"}
                                            placeholder={getTranslation("PLACEHOLDER_ENTER_CITY_ADDRESS") || "Enter city address"}
                                            {...form.getInputProps('contractorCityAddress')}
                                        />
                                        <TextInput
                                        // Contractor VAT Number
                                            label={getTranslation("LABEL_VAT_NUMBER") || "VAT Number"}
                                            placeholder={getTranslation("PLACEHOLDER_ENTER_VAT_NUMBER") || "Enter VAT number"}
                                            {...form.getInputProps('contractorVatNumber')}
                                        />
                                    </Stack>
                                </Paper>
                            </Stack>

                            <Stack gap={2}>
                                {/* Financial Info */}
                                <Title order={5}>{getTranslation("LABEL_CONTRACT_FORM_FINANCIAL_INFO") || "Financial Info"}</Title>
                                <Paper withBorder p="md" radius="md">
                                    <Stack gap="md">
                                        <NumberInput
                                        // Default Invoicing Number
                                            label={getTranslation("LABEL_INVOICING_NUMBER") || "Invoicing Number"}
                                            placeholder={getTranslation("PLACEHOLDER_ENTER_INVOICING_NUMBER") || "Enter invoicing number"}
                                            min={0}
                                            hideControls
                                            {...form.getInputProps('defaultInvoicingNumber')}
                                        />
                                        <NumberInput
                                        // Total Price
                                            label={getTranslation("LABEL_TOTAL_VALUE_") || "Total Value (€)"}
                                            placeholder={getTranslation("PLACEHOLDER_ENTER_TOTAL_VALUE") || "Enter total value"}
                                            decimalScale={2}
                                            min={0}
                                            hideControls
                                            {...form.getInputProps('totalPrice')}
                                        />
                                        <Select
                                        // Invoicing VAT ID
                                            label={getTranslation("LABEL_VAT") || "VAT"}
                                            placeholder={getTranslation("PLACEHOLDER_SELECT_VAT") || "Select VAT"}
                                            data={vatOptions.map(option => ({
                                                value: option.id.toString(),
                                                label: option.description
                                            }))}
                                            value={form.values.invoicingVatId?.toString() || null}
                                            onChange={(value) => form.setFieldValue('invoicingVatId', value ? parseInt(value) : undefined)}
                                            clearable
                                        />
                                        <Select
                                        // Invoicing Payment Term ID
                                            label={getTranslation("LABEL_RECEIPT") || "Receipt"}
                                            placeholder={getTranslation("PLACEHOLDER_SELECT_RECEIPT") || "Select receipt"}
                                            data={paymentTermOptions.map(option => ({
                                                value: option.id.toString(),
                                                label: option.description
                                            }))}
                                            value={form.values.invoicingPaymentTermId?.toString() || null}
                                            onChange={(value) => form.setFieldValue('invoicingPaymentTermId', value ? parseInt(value) : undefined)}
                                            clearable
                                        />
                                    </Stack>
                                </Paper>
                            </Stack>

                            <Stack gap={2}>
                                {/* Automation & Workflows */}
                                <Title order={5}>{getTranslation("LABEL_CONTRACT_FORM_AUTOMATION_WORKFLOWS") || "Automation & Workflows"}</Title>
                                <Paper withBorder p="md" radius="md">
                                    <Stack gap="md">
                                        <Checkbox
                                        // Include In Report
                                            label={getTranslation("LABEL_INCLUDE_IN_SUMMARIES") || "Include In Summaries"}
                                            {...form.getInputProps('includeInReport', { type: 'checkbox' })}
                                        />
                                        <Checkbox
                                        // Copy To Ramapp
                                            label={getTranslation("LABEL_COPY_TO_RAMAPP") || "Copy To Ramapp"}
                                            {...form.getInputProps('copyToRamapp', { type: 'checkbox' })}
                                        />
                                        <Select
                                        // User Template Contract
                                            label={getTranslation("LABEL_COPY_USERS_FROM_TEMPLATE_CONTRACT") || "Copy users from Template Contract"}
                                            placeholder={getTranslation("PLACEHOLDER_SELECT_TEMPLATE_CONTRACT") || "Select template contract"}
                                            data={templateContracts.map(tc => ({
                                                value: tc.id.toString(),
                                                label: tc.name
                                                // + (tc.contractStatusDefaultValue ? ' (Active)' : ' (Inactive)')
                                            }))}
                                            value={form.values.userTemplateContractId !== undefined ? String(form.values.userTemplateContractId) : null}
                                            onChange={value => form.setFieldValue('userTemplateContractId', value ? Number(value) : undefined)}
                                            clearable
                                            searchable
                                        />
                                    </Stack>
                                </Paper>
                            </Stack>
                        </Stack>
                    </Grid.Col>
                </Grid>
            </form>
        </Paper>
    );
}

// Legacy default export for backward compatibility
export default function ContractForm({ contract, mode, addressSystemId, onClose, onBack }: {
    contract?: ContractResponse | null;
    mode: 'create' | 'edit';
    addressSystemId: number;
    onClose: () => void;
    onBack: () => void;
}) {
    if (mode === 'create') {
        return <AddContractForm addressSystemId={addressSystemId} onClose={onClose} onBack={onBack} />;
    } else if (contract) {
        return <EditContractForm contract={contract} addressSystemId={addressSystemId} onClose={onClose} onBack={onBack} />;
    }
    return null;
}
